import dayjs from 'dayjs';
import { Building2, Calendar, User, Phone, MapPin } from 'lucide-react';
import { memo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ITodayOffersForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import { useDialogStore, useAuth } from '@/store';
import AvatarToolTip from '@/UI/AvatarToolTip';

type ClientSectionProps = {
  client: ITodayOffersForRequest;
};

// Info item component to reduce repeated code
const InfoItem = memo(
  ({
    icon: Icon,
    label,
    children,
  }: {
    icon: React.ElementType;
    label: string;
    children: React.ReactNode;
  }) => (
    <div className="flex items-center gap-1.5">
      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/5">
        <Icon className="h-2.5 w-2.5 text-primary" />
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground">{label}</p>
        {children}
      </div>
    </div>
  ),
);

InfoItem.displayName = 'InfoItem';

export const ClientSection = memo(({ client }: ClientSectionProps) => {
  const { openClientCardDialog, openOneOffer } = useDialogStore();
  const { user } = useAuth();

  const handleOpenClientCard = () => {
    if (client?.ClientID) {
      openClientCardDialog(client.ClientID);
    }
  };

  const handleOpenOffer = () => {
    if (client._id) {
      openOneOffer(client._id);
    }
  };

  return (
    <div className="space-y-2">
      {user && (
        <div className="flex items-center gap-2 rounded-md bg-muted/10 p-1.5">
          <AvatarToolTip
            id={user?._id}
            size={32}
            variant="circular"
            avatar={user?.avatar}
            empName={user?.empName}
            jobName={user?.job?.jobName}
          />
          <div>
            <p className="text-xs font-medium">{user?.empName}</p>
            <p className="text-[10px] text-muted-foreground">
              {user?.job?.jobName}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2 rounded-md bg-gradient-to-br from-muted/10 via-muted/20 to-muted/10 p-2 shadow-inner">
        <InfoItem icon={User} label="إسم العميل">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="link"
                  className="h-auto p-0 text-xs font-medium transition-colors hover:text-primary"
                  onClick={handleOpenClientCard}
                >
                  {client.ClientName}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                فتح بطاقة العميل
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </InfoItem>

        <InfoItem icon={Building2} label="رقم العرض">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="link"
                  className="h-auto p-0 text-xs font-medium transition-colors hover:text-primary"
                  onClick={handleOpenOffer}
                >
                  {client._id}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                فتح تفاصيل العرض
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </InfoItem>

        {(client as any).ClientMobile && (
          <InfoItem icon={Phone} label="رقم الهاتف">
            <p className="text-xs font-medium">
              {(client as any).ClientMobile}
            </p>
          </InfoItem>
        )}

        {(client as any).Address && (
          <InfoItem icon={MapPin} label="العنوان">
            <p className="line-clamp-2 text-xs font-medium">
              {(client as any).Address}
            </p>
          </InfoItem>
        )}

        <InfoItem icon={Calendar} label="تاريخ الطلبية">
          <Badge
            variant="outline"
            className="mt-0.5 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary"
          >
            {dayjs().format('YYYY-MM-DD')}
          </Badge>
        </InfoItem>
      </div>
    </div>
  );
});

ClientSection.displayName = 'ClientSection';
